// src/onboarding/transitionSteps.ts
import type { OnboardingStepDeps } from '@/composables/useOnboarding';
import i18n from '@/i18n';
import type { StepOptions } from 'shepherd.js';
import type { Composer } from 'vue-i18n';

const t = (key: string) => (i18n.global as unknown as Composer).t(key);

export function getTransitionSteps(deps: OnboardingStepDeps): StepOptions[] {
  // Récupère les fonctions nécessaires de 'deps' pour les transitions
  const { tour, markChapterAsComplete, showChapterNavigation, skipToNextChapter } = deps;

  return [
    {
      id: 'transition-1-2',
      title: t('onboarding.transitions.transition-1-2.title'),
      text: t('onboarding.transitions.transition-1-2.text'),
      buttons: [
        {
          text: t('onboarding.transitions.buttonLabel.navigate'),
          action: () => {
            markChapterAsComplete('01_Introduction');
            showChapterNavigation();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.quit'),
          action: () => {
            markChapterAsComplete('01_Introduction');
            tour.value?.cancel();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.continue'),
          action: () => {
            skipToNextChapter('01_Introduction');
          },
        },
      ],
    },
    {
      id: 'transition-2-3',
      title: t('onboarding.transitions.transition-2-3.title'),
      text: t('onboarding.transitions.transition-2-3.text'),
      buttons: [
        {
          text: t('onboarding.transitions.buttonLabel.navigate'),
          action: () => {
            markChapterAsComplete('02_RecipeGeneration');
            showChapterNavigation();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.quit'),
          action: () => {
            markChapterAsComplete('02_RecipeGeneration');
            tour.value?.cancel();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.continue'),
          action: () => {
            skipToNextChapter('02_RecipeGeneration');
          },
        },
      ],
    },
    {
      id: 'transition-3-4',
      title: t('onboarding.transitions.transition-3-4.title'),
      text: t('onboarding.transitions.transition-3-4.text'),
      buttons: [
        {
          text: t('onboarding.transitions.buttonLabel.navigate'),
          action: () => {
            markChapterAsComplete('03_RecipeDetails');
            showChapterNavigation();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.quit'),
          action: () => {
            markChapterAsComplete('03_RecipeDetails');
            tour.value?.cancel();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.continue'),
          action: () => {
            skipToNextChapter('03_RecipeDetails');
          },
        },
      ],
    },
    {
      id: 'transition-4-5',
      title: t('onboarding.transitions.transition-4-5.title'),
      text: t('onboarding.transitions.transition-4-5.text'),
      buttons: [
        {
          text: t('onboarding.transitions.buttonLabel.navigate'),
          action: () => {
            markChapterAsComplete('04_RecipeActions');
            showChapterNavigation();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.quit'),
          action: () => {
            markChapterAsComplete('04_RecipeActions');
            tour.value?.cancel();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.continue'),
          action: () => {
            skipToNextChapter('04_RecipeActions');
          },
        },
      ],
    },
    {
      id: 'transition-5-6',
      title: t('onboarding.transitions.transition-5-6.title'),
      text: t('onboarding.transitions.transition-5-6.text'),
      buttons: [
        {
          text: t('onboarding.transitions.buttonLabel.navigate'),
          action: () => {
            markChapterAsComplete('05_Organization');
            showChapterNavigation();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.quit'),
          action: () => {
            markChapterAsComplete('05_Organization');
            tour.value?.cancel();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.continue'),
          action: () => {
            skipToNextChapter('05_Organization');
          },
        },
      ],
    },
    {
      id: 'transition-6-7',
      title: t('onboarding.transitions.transition-6-7.title'),
      text: t('onboarding.transitions.transition-6-7.text'),
      buttons: [
        {
          text: t('onboarding.transitions.buttonLabel.navigate'),
          action: () => {
            markChapterAsComplete('06_Scan');
            showChapterNavigation();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.quit'),
          action: () => {
            markChapterAsComplete('06_Scan');
            tour.value?.cancel();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.continue'),
          action: () => {
            skipToNextChapter('06_Scan');
          },
        },
      ],
    },
    {
      id: 'transition-7-8',
      title: t('onboarding.transitions.transition-7-8.title'),
      text: t('onboarding.transitions.transition-7-8.text'),
      buttons: [
        {
          text: t('onboarding.transitions.buttonLabel.navigate'),
          action: () => {
            markChapterAsComplete('07_ProfileAndTeam');
            showChapterNavigation();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.quit'),
          action: () => {
            markChapterAsComplete('07_ProfileAndTeam');
            tour.value?.cancel();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.continue'),
          action: () => {
            skipToNextChapter('07_ProfileAndTeam');
          },
        },
      ],
    },
    {
      id: 'transition-8-9',
      title: t('onboarding.transitions.transition-8-9.title'),
      text: t('onboarding.transitions.transition-8-9.text'),
      buttons: [
        {
          text: t('onboarding.transitions.buttonLabel.navigate'),
          action: () => {
            markChapterAsComplete('08_Planning');
            showChapterNavigation();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.quit'),
          action: () => {
            markChapterAsComplete('08_Planning');
            tour.value?.cancel();
          },
          secondary: true,
        },
        {
          text: t('onboarding.transitions.buttonLabel.continue'),
          action: () => {
            skipToNextChapter('08_Planning');
          },
        },
      ],
    },
  ];
}
